import { useEffect, useRef, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton } from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useNavigate } from "react-router-dom";

import { userState } from "../../interface/MainInterface";
import { HttpMainApi } from "../../interface/main-api";
import Toast from "../../utils/Toast";
import { BookSection, categoryLabels, DEFAULT_BOOK_IMAGE, isBookSection } from "./book-home";

interface Props {
  userState: userState;
  view?: "summary" | "detail" | "library-detail" | "popular-detail";
}

interface ActiveBookLoan {
  loanId: string;
  bookSk?: string;
  title: string;
  image?: string;
  borrowedAt: string;
  dueAt?: string;
}

interface ActiveLibraryBookLoan {
  bookSk: string;
  title: string;
  image?: string;
  section?: BookSection;
}

interface PopularLibraryBook {
  rank: number;
  bookSk: string;
  title: string;
  image?: string;
  loanCount: number;
  isReadByMe?: boolean;
}

const mainApi = new HttpMainApi();
const KOREA_TIME_ZONE = "Asia/Seoul";
const LOAN_PERIOD_DAYS = 7;

function createRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// TODO: 다권 대여 카드 검증이 끝나면 개발용 20권 미리보기를 제거한다.
const createTwentyLoanPreview = (): ActiveBookLoan[] => Array.from({ length: 20 }, (_, index) => ({
  loanId: `preview-loan-${index + 1}`,
  title: `대여중인 테스트 도서 ${index + 1}`,
  image: DEFAULT_BOOK_IMAGE,
  borrowedAt: `2026-08-${String(12 - (index % 7)).padStart(2, "0")}T01:00:00Z`,
}));

function isTwentyLoanPreview() {
  return process.env.NODE_ENV !== "production"
    && new URLSearchParams(window.location.search).get("preview") === "20";
}

function calendarDayIndex(value: Date) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: KOREA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const part = (type: string) => Number(parts.find((item) => item.type === type)?.value);
  return Date.UTC(part("year"), part("month") - 1, part("day")) / 86_400_000;
}

export function getRemainingLoanDays(borrowedAt: string, now = new Date()) {
  const borrowed = new Date(borrowedAt);
  if (Number.isNaN(borrowed.getTime()) || Number.isNaN(now.getTime())) return 0;
  return Math.max(0, LOAN_PERIOD_DAYS - (calendarDayIndex(now) - calendarDayIndex(borrowed)));
}

function formatBorrowedAt(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "대여일 정보 없음";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: KOREA_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? "";
  return `대여일 ${part("year")}.${part("month")}.${part("day")}`;
}

function getCategoryLabel(section?: BookSection) {
  return section && isBookSection(section) ? categoryLabels[section] : "도서";
}

function getLibraryLoanResponse(response: any) {
  const result = response?.response?.result;
  return {
    totalCount: typeof result?.totalCount === "number" ? result.totalCount : 0,
    books: Array.isArray(result?.books) ? result.books as ActiveLibraryBookLoan[] : [],
  };
}

function getPopularBookResponse(response: any) {
  const books = response?.response?.result?.books;
  return Array.isArray(books) ? books as PopularLibraryBook[] : [];
}

const MobileBookLoanStatus = ({ userState, view = "summary" }: Props) => {
  const navigate = useNavigate();
  const isPersonalDetail = view === "detail";
  const isLibraryDetail = view === "library-detail";
  const isPopularDetail = view === "popular-detail";
  const [loans, setLoans] = useState<ActiveBookLoan[]>([]);
  const [personalStatus, setPersonalStatus] = useState<"loading" | "ready" | "error">("loading");
  const [libraryLoans, setLibraryLoans] = useState<ActiveLibraryBookLoan[]>([]);
  const [libraryTotalCount, setLibraryTotalCount] = useState(0);
  const [libraryStatus, setLibraryStatus] = useState<"loading" | "ready" | "error">("loading");
  const [popularBooks, setPopularBooks] = useState<PopularLibraryBook[]>([]);
  const [popularStatus, setPopularStatus] = useState<"loading" | "ready" | "error">("loading");
  const [selectedReturnLoan, setSelectedReturnLoan] = useState<ActiveBookLoan | null>(null);
  const [returnStatus, setReturnStatus] = useState<"idle" | "saving">("idle");
  const [returnError, setReturnError] = useState("");
  const toastRef = useRef<any>();
  const hasLoggedSummaryEntry = useRef(false);

  useEffect(() => {
    if (view !== "summary" || hasLoggedSummaryEntry.current) return;
    hasLoggedSummaryEntry.current = true;

    try {
      void mainApi.put_dashboard_event({
        eventType: "BOOK_LOAN_STATUS_VIEW",
        userId: userState.userId,
        userName: userState.userName,
        userDong: userState.userDong,
        userHo: userState.userHo,
        path: "/mobilebookstats",
        source: "MOBILE_BOOK_LOAN_STATUS",
      }).catch(() => undefined);
    } catch {
      // Logging must never prevent the page from opening.
    }
  }, [userState.userDong, userState.userHo, userState.userId, userState.userName, view]);

  useEffect(() => {
    if (isLibraryDetail || isPopularDetail) return;
    if (isTwentyLoanPreview()) {
      setLoans(createTwentyLoanPreview());
      setPersonalStatus("ready");
      return;
    }

    let active = true;
    const load = async () => {
      setPersonalStatus("loading");
      try {
        const response = await mainApi.get_my_active_book_loans({ userId: userState.userId });
        if (response.code !== "200") throw new Error();
        if (!active) return;
        const result = Array.isArray(response.response?.result) ? response.response.result : [];
        setLoans(result as ActiveBookLoan[]);
        setPersonalStatus("ready");
      } catch {
        if (active) setPersonalStatus("error");
      }
    };
    void load();
    return () => { active = false; };
  }, [isLibraryDetail, isPopularDetail, userState.userId]);

  useEffect(() => {
    if (isPersonalDetail || isPopularDetail) return;
    let active = true;
    const load = async () => {
      setLibraryStatus("loading");
      try {
        const response = await mainApi.get_active_library_book_loans({});
        if (response.code !== "200") throw new Error();
        if (!active) return;
        const result = getLibraryLoanResponse(response);
        setLibraryLoans(result.books);
        setLibraryTotalCount(result.totalCount);
        setLibraryStatus("ready");
      } catch {
        if (active) setLibraryStatus("error");
      }
    };
    void load();
    return () => { active = false; };
  }, [isPersonalDetail, isPopularDetail]);

  useEffect(() => {
    if (isPersonalDetail || isLibraryDetail) return;
    let active = true;
    const load = async () => {
      setPopularStatus("loading");
      try {
        const response = await mainApi.get_popular_library_books({ userId: userState.userId });
        if (response.code !== "200") throw new Error();
        if (!active) return;
        setPopularBooks(getPopularBookResponse(response));
        setPopularStatus("ready");
      } catch {
        if (active) setPopularStatus("error");
      }
    };
    void load();
    return () => { active = false; };
  }, [isLibraryDetail, isPersonalDetail, userState.userId]);

  const openReturnConfirm = (loan: ActiveBookLoan) => {
    setReturnError("");
    setReturnStatus("idle");
    setSelectedReturnLoan(loan);
  };

  const closeReturnConfirm = () => {
    if (returnStatus === "saving") return;
    setSelectedReturnLoan(null);
    setReturnError("");
  };

  const confirmReturn = async () => {
    const loan = selectedReturnLoan;
    if (!loan || returnStatus === "saving") return;
    if (!loan.bookSk) {
      setReturnError("반납 정보를 찾을 수 없습니다.");
      return;
    }

    setReturnStatus("saving");
    setReturnError("");
    try {
      const response = await mainApi.return_my_book_loan({
        bookSk: loan.bookSk,
        loanId: loan.loanId,
        requestId: createRequestId(),
        userId: userState.userId,
        userName: userState.userName,
        userDong: userState.userDong,
        userHo: userState.userHo,
      });
      if (response.code !== "200") throw new Error(response.response?.error_msg || "반납 처리에 실패했습니다.");

      setLoans((currentLoans) => currentLoans.filter((currentLoan) => currentLoan.loanId !== loan.loanId));
      setLibraryLoans((currentLoans) => currentLoans.filter((currentLoan) => currentLoan.bookSk !== loan.bookSk));
      setLibraryTotalCount((count) => Math.max(0, count - 1));
      setSelectedReturnLoan(null);
      setReturnStatus("idle");
      toastRef.current?.toast("반납 처리되었습니다.", "success", 2000, {
        vertical: "bottom",
        horizontal: "center",
      });
    } catch (error) {
      setReturnStatus("idle");
      setReturnError(error instanceof Error ? error.message : "반납 처리에 실패했습니다.");
    }
  };

  const visibleLoans = isPersonalDetail ? loans : loans.slice(0, 3);
  const visibleLibraryLoans = isLibraryDetail ? libraryLoans : libraryLoans.slice(0, 3);
  const visiblePopularBooks = isPopularDetail ? popularBooks : popularBooks.slice(0, 5);
  const listLabel = isPersonalDetail ? "대여중인 도서 전체 목록" : "최근 대여 도서 목록";

  return (
    <main className={`hpoint-renewal hp-book-loan-status-page${isPersonalDetail || isLibraryDetail || isPopularDetail ? " hp-book-loan-status-page--detail" : ""}`}>
      {!isLibraryDetail && !isPopularDetail ? <section className="hp-book-loan-status" aria-labelledby="book-loan-status-heading">
        <div className="hp-book-loan-status__heading">
          <div>
            <span>{isPersonalDetail ? "작은 도서관 · 대여 목록" : "작은 도서관"}</span>
            <h1 id="book-loan-status-heading">{isPersonalDetail ? "대여중인 도서" : "현재 대여중인 도서"}</h1>
          </div>
          {personalStatus === "ready" ? <strong>{isPersonalDetail ? `${loans.length}권 대여중` : `${loans.length}권`}</strong> : null}
        </div>

        {personalStatus === "loading" ? (
          <div className="hp-book-loan-status__card hp-book-loan-status__skeleton" aria-label="대여중인 도서 불러오는 중" role="status">
            <Skeleton variant="rounded" width={104} height={72} />
            <div><Skeleton width="78%" height={22} /><Skeleton width="48%" /></div>
          </div>
        ) : null}

        {personalStatus === "error" ? <p className="hp-book-loan-status__message" role="alert">대여중인 도서를 불러오지 못했습니다.</p> : null}
        {personalStatus === "ready" && loans.length === 0 ? <p className="hp-book-loan-status__message">현재 대여중인 도서가 없습니다.</p> : null}

        {personalStatus === "ready" && loans.length > 0 ? (
          <div className={`hp-book-loan-status__list${isPersonalDetail ? " hp-book-loan-status__detail-list" : ""}`} aria-label={listLabel}>
            {visibleLoans.map((loan) => {
              const remainingDays = getRemainingLoanDays(loan.borrowedAt);
              return (
                <article className={`${isPersonalDetail ? "hp-book-loan-status__detail-item" : "hp-book-loan-status__card hp-book-loan-status__item"}`} key={loan.loanId}>
                  <img
                    src={loan.image || DEFAULT_BOOK_IMAGE}
                    alt={`${loan.title} 표지`}
                    onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = DEFAULT_BOOK_IMAGE; }}
                  />
                  <div className="hp-book-loan-status__content">
                    <div className="hp-book-loan-status__summary">
                      <h2 className="hp-book-loan-status__title">{loan.title}</h2>
                      <button
                        className="hp-book-loan-status__return-button"
                        type="button"
                        aria-label={`${loan.title} 반납`}
                        onClick={() => openReturnConfirm(loan)}
                      >
                        반납
                      </button>
                    </div>
                    <div className="hp-book-loan-status__personal-meta">
                      <p>{formatBorrowedAt(loan.borrowedAt)}</p>
                      <strong>반납까지 {remainingDays}일</strong>
                    </div>
                    <div className="hp-book-loan-status__progress" aria-label={`반납까지 ${remainingDays}일`}>
                      <span style={{ width: `${(remainingDays / LOAN_PERIOD_DAYS) * 100}%` }} />
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : null}

        {!isPersonalDetail && personalStatus === "ready" && loans.length > 3 ? (
          <button
            className="hp-book-loan-status__all-button"
            type="button"
            onClick={() => navigate({ pathname: "/mobilebookstats/loans", search: window.location.search })}
          >
            전체 대여 도서 {loans.length}권 보기
            <ChevronRightRoundedIcon aria-hidden="true" />
          </button>
        ) : null}
      </section> : null}

      {!isPersonalDetail && !isPopularDetail ? <section className={`hp-book-loan-status hp-book-loan-status__library-section${isLibraryDetail ? " hp-book-loan-status__library-section--detail" : ""}`} aria-labelledby="library-book-loan-status-heading">
        <div className="hp-book-loan-status__heading hp-book-loan-status__library-heading">
          <div>
            <span>{isLibraryDetail ? "작은 도서관 · 전체 대여 목록" : "작은 도서관"}</span>
            <h1 id="library-book-loan-status-heading">전체 대여중인 도서</h1>
          </div>
          {libraryStatus === "ready" ? <strong>{isLibraryDetail ? `${libraryTotalCount}권 대여중` : `${libraryTotalCount}권`}</strong> : null}
        </div>

        {libraryStatus === "loading" ? (
          <div className="hp-book-loan-status__card hp-book-loan-status__skeleton" aria-label="전체 대여중인 도서 불러오는 중" role="status">
            <Skeleton variant="rounded" width={104} height={72} />
            <div><Skeleton width="78%" height={22} /><Skeleton width="48%" /></div>
          </div>
        ) : null}
        {libraryStatus === "error" ? <p className="hp-book-loan-status__message" role="alert">전체 대여중인 도서를 불러오지 못했습니다.</p> : null}
        {libraryStatus === "ready" && libraryTotalCount === 0 ? <p className="hp-book-loan-status__message">현재 전체 대여중인 도서가 없습니다.</p> : null}

        {libraryStatus === "ready" && visibleLibraryLoans.length > 0 ? (
          <div className={`hp-book-loan-status__library-list${isLibraryDetail ? " hp-book-loan-status__detail-list" : ""}`} aria-label={isLibraryDetail ? "전체 대여중인 도서 전체 목록" : "전체 대여중인 도서 미리보기"}>
            {visibleLibraryLoans.map((loan) => (
              <article className={isLibraryDetail ? "hp-book-loan-status__detail-item hp-book-loan-status__library-item" : "hp-book-loan-status__card hp-book-loan-status__library-card"} key={loan.bookSk}>
                <img
                  src={loan.image || DEFAULT_BOOK_IMAGE}
                  alt={`${loan.title} 표지`}
                  onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = DEFAULT_BOOK_IMAGE; }}
                />
                <div className="hp-book-loan-status__content">
                  <div className="hp-book-loan-status__summary">
                    <h2 className="hp-book-loan-status__title">{loan.title}</h2>
                    <strong>대여중</strong>
                  </div>
                  <p>{getCategoryLabel(loan.section)}</p>
                </div>
              </article>
            ))}
          </div>
        ) : null}

        {!isLibraryDetail && libraryStatus === "ready" && libraryTotalCount > 3 ? (
          <button
            className="hp-book-loan-status__all-button"
            type="button"
            onClick={() => navigate("/mobilebookstats/library-loans")}
          >
            전체 대여중인 도서 {libraryTotalCount}권 보기
            <ChevronRightRoundedIcon aria-hidden="true" />
          </button>
        ) : null}
      </section> : null}

      {!isPersonalDetail && !isLibraryDetail ? <section className={`hp-book-loan-status hp-book-loan-status__popular-section${isPopularDetail ? " hp-book-loan-status__popular-section--detail" : ""}`} aria-labelledby="popular-book-heading">
        <div className="hp-book-loan-status__heading">
          <div>
            <span>{isPopularDetail ? "작은 도서관 · 누적 대여 순위" : "작은 도서관"}</span>
            <h1 id="popular-book-heading">인기 도서</h1>
          </div>
          {popularStatus === "ready" && popularBooks.length > 0 ? <strong>TOP {isPopularDetail ? 10 : 5}</strong> : null}
        </div>

        {popularStatus === "loading" ? (
          <div className="hp-book-loan-status__card hp-book-loan-status__skeleton" aria-label="인기 도서 불러오는 중" role="status">
            <Skeleton variant="rounded" width={104} height={72} />
            <div><Skeleton width="78%" height={22} /><Skeleton width="48%" /></div>
          </div>
        ) : null}
        {popularStatus === "error" ? <p className="hp-book-loan-status__message" role="alert">인기 도서를 불러오지 못했습니다.</p> : null}
        {popularStatus === "ready" && popularBooks.length === 0 ? <p className="hp-book-loan-status__message">아직 대여 이력이 없습니다.</p> : null}

        {popularStatus === "ready" && visiblePopularBooks.length > 0 ? (
          <div className={`hp-book-loan-status__popular-list${isPopularDetail ? " hp-book-loan-status__detail-list" : ""}`} aria-label={`인기 도서 TOP ${isPopularDetail ? 10 : 5}`}>
            {visiblePopularBooks.map((book) => (
              <article className={isPopularDetail ? "hp-book-loan-status__detail-item hp-book-loan-status__popular-item" : "hp-book-loan-status__card hp-book-loan-status__popular-card"} key={book.bookSk}>
                <span className="hp-book-loan-status__rank hp-book-loan-status__rank--index" aria-label={`${book.rank}위`}>
                  {String(book.rank).padStart(2, "0")}
                </span>
                <img
                  src={book.image || DEFAULT_BOOK_IMAGE}
                  alt={`${book.title} 표지`}
                  onError={(event) => { event.currentTarget.onerror = null; event.currentTarget.src = DEFAULT_BOOK_IMAGE; }}
                />
                <div className="hp-book-loan-status__content">
                  <h2 className="hp-book-loan-status__title">{book.title}</h2>
                  <p>{book.loanCount}회 대여</p>
                </div>
                {book.isReadByMe ? <span className="hp-book-loan-status__read-stamp" aria-label="읽음">읽음</span> : null}
              </article>
            ))}
          </div>
        ) : null}

        {!isPopularDetail && popularStatus === "ready" && popularBooks.length > 5 ? (
          <button className="hp-book-loan-status__all-button" type="button" onClick={() => navigate("/mobilebookstats/popular-books")}>
            인기 도서 TOP 10 보기
            <ChevronRightRoundedIcon aria-hidden="true" />
          </button>
        ) : null}
      </section> : null}

      <Dialog open={Boolean(selectedReturnLoan)} onClose={closeReturnConfirm} fullWidth maxWidth="xs">
        <DialogTitle>이 책을 반납하시겠습니까?</DialogTitle>
        <DialogContent>
          {selectedReturnLoan ? (
            <div className="hp-book-scan-confirm">
              <img src={selectedReturnLoan.image || DEFAULT_BOOK_IMAGE} alt={`${selectedReturnLoan.title} 표지`} />
              <div>
                <strong>{selectedReturnLoan.title}</strong>
                <span>반납하면 대여 목록에서 삭제됩니다.</span>
              </div>
            </div>
          ) : null}
          {returnError ? <p className="hp-book-scanner__error" role="alert">{returnError}</p> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReturnConfirm} disabled={returnStatus === "saving"}>취소</Button>
          <Button className="hp-button hp-button--primary" onClick={() => void confirmReturn()} disabled={returnStatus === "saving"}>
            반납하기
          </Button>
        </DialogActions>
      </Dialog>
      <Toast ref={toastRef} />
    </main>
  );
};

export default MobileBookLoanStatus;
